import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import './Coupons.css';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    expiry_date: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://gokulnair.com/new_project/api/coupons');
      setCoupons(response.data);
    } catch (err) {
      setError('Failed to fetch coupons');
      console.error('Error fetching coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFormChange = (e) => {
    setCreateForm({
      ...createForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post('https://gokulnair.com/new_project/api/admin/add-coupon', {
        code: createForm.code,
        discount_type: createForm.discount_type,
        discount_value: parseFloat(createForm.discount_value),
        expiry_date: createForm.expiry_date
      });

      if (response.data.message === 'Coupon added successfully') {
        // Reset form and refresh coupons
        setCreateForm({
          code: '',
          discount_type: 'percentage',
          discount_value: '',
          expiry_date: ''
        });
        setShowCreateForm(false);
        fetchCoupons();
        alert('Coupon created successfully!');
      }
    } catch (err) {
      console.error('Error creating coupon:', err);
      alert('Failed to create coupon. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const getDiscountTypeText = (type) => {
    return type === 'percentage' ? 'Percentage' : 'Fixed Amount';
  };

  const getActiveStatusText = (isActive) => {
    return isActive === 1 ? 'Active' : 'Inactive';
  };

  const getActiveStatusClass = (isActive) => {
    return isActive === 1 ? 'status-active' : 'status-inactive';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading coupons...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="coupons-content">
        {error && <div className="error-message">{error}</div>}
        
        <div className="coupons-header">
          <div className="header-left">
            <h1>Coupons Management</h1>
            <p>Manage discount coupons for your store</p>
          </div>
          <button 
            className="create-coupon-btn"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ Create Coupon'}
          </button>
        </div>

        {showCreateForm && (
          <div className="create-coupon-form">
            <h2>Create New Coupon</h2>
            <form onSubmit={handleCreateCoupon}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="code">Coupon Code</label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={createForm.code}
                    onChange={handleCreateFormChange}
                    required
                    placeholder="e.g., WELCOME20"
                    maxLength="20"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="discount_type">Discount Type</label>
                  <select
                    id="discount_type"
                    name="discount_type"
                    value={createForm.discount_type}
                    onChange={handleCreateFormChange}
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="discount_value">
                    Discount Value {createForm.discount_type === 'percentage' ? '(%)' : '($)'}
                  </label>
                  <input
                    type="number"
                    id="discount_value"
                    name="discount_value"
                    value={createForm.discount_value}
                    onChange={handleCreateFormChange}
                    required
                    placeholder={createForm.discount_type === 'percentage' ? '20' : '10.00'}
                    min="0"
                    step={createForm.discount_type === 'percentage' ? '1' : '0.01'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="expiry_date">Expiry Date</label>
                  <input
                    type="date"
                    id="expiry_date"
                    name="expiry_date"
                    value={createForm.expiry_date}
                    onChange={handleCreateFormChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="coupons-section">
          <h2>All Coupons ({coupons.length})</h2>
          <div className="coupons-grid">
            {coupons.length === 0 ? (
              <div className="no-coupons">
                <p>No coupons found. Create your first coupon!</p>
              </div>
            ) : (
              coupons.map((coupon) => (
                <div key={coupon.id} className="coupon-card">
                  <div className="coupon-header">
                    <div className="coupon-code">
                      <h3>{coupon.code}</h3>
                      <span className={`status-badge ${getActiveStatusClass(coupon.is_active)}`}>
                        {getActiveStatusText(coupon.is_active)}
                      </span>
                    </div>
                    <div className="coupon-discount">
                      <span className="discount-value">
                        {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}
                      </span>
                      <span className="discount-type">{getDiscountTypeText(coupon.discount_type)}</span>
                    </div>
                  </div>
                  
                  <div className="coupon-details">
                    <div className="detail-item">
                      <label>Expiry Date:</label>
                      <span>{formatDate(coupon.expiry_date)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Created:</label>
                      <span>{formatDate(coupon.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Coupons; 